import Util from '../utils/util';
import Tool from '../tools/Tool';
import Operation from './operation/Operation';
import OperationController from './operation/OperationController';
import GeometryAlgorithm from '../geometry/GeometryAlgorithm';
import EditResult from './EditResult';
import CheckController from './check/CheckController';

/**
 * 编辑工具类
 * @interface
 */
export default class EditTool extends Tool {
    /**
     * 构造方法
     * @param {object} options 可选项
     */
    constructor(options = {}) {
        super({
            runtimeRules: [], // 运行时检查
            postRules: [], // 完成时检查
            ...options
        });

        this.geometryAlgorithm = GeometryAlgorithm.getInstance();

        /**
         * 编辑信息
         * @type {object}
         */
        this.editResult = null;

        /**
         * 记录最原始的编辑信息
         * @type {object}
         */
        this._originEditResult = null;
    }

    /**
     * 添加到控制器时调用，执行数据的初始化
     * @param {object} map 地图实例
     * @param {object} controller 控制器实例
     */
    onAdd(controller) {
        super.onAdd(controller);

        this._featureSelector = this._map.FeatureSelector;
        this._operationController = new OperationController();
        this._geojsonConverter = this._map.GeojsonConverter;
        this._checkController = new CheckController(this._map);
    }

    /**
     * 从控制器中删除时调用，执行数据清理
     * @param {object} map        地图实例
     * @param {object} controller 控制器实例
     */
    onRemove() {
        this._featureSelector = null;
        this._geojsonConverter = null;

        this._operationController.clear();
        this._operationController = null;

        this._checkController.clear();
        this._checkController = null;

        super.onRemove();
    }

    bindEvent(map) {
        map.on('keyup', this.onKeyUp, this);
    }

    unbindEvent(map) {
        map.off('keyup', this.onKeyUp, this);
    }

    /**
     * 加载检查规则
     * @param {*} rules      检查规则
     * @param {*} situation  执行时机，1：运行时；2.完成时；
     */
    installCheckRule(rules = [], situation = 1) {
        rules.forEach(rule => {
            this._checkController.add(rule, situation);
        });
    }

    /**
     * 初始化工具数据
     * @param {*} data
     */
    initialize(data) {
        this.editResult = new EditResult(data);
        this._originEditResult = this.editResult.clone();

        if (this.options.runtimeRules) {
            this.installCheckRule(this.options.runtimeRules, 1);
        }
        if (this.options.postRules) {
            this.installCheckRule(this.options.postRules, 2);
        }

        this.createOperation('初始化工具');
    }

    /**
     * 清空编辑数据，回到初始化状态
     */
    clear() {
        this.resetEditResult(this._originEditResult);

        this._operationController.clear();

        this.createOperation('重置工具');
    }

    onKeyUp(keyboardEvent) {
        super.onKeyUp(keyboardEvent);

        const event = keyboardEvent.originalEvent;
        switch (event.key) {
            case ' ':
                this.finish();
                break;
            case 'Escape':
                this.clear();
                break;
            case 'z':
                if (event.ctrlKey) {
                    this.undo();
                }
                break;
            case 'x':
                if (event.ctrlKey) {
                    this.redo();
                }
                break;
            default:
                break;
        }
    }

    /**
     * 执行运行时检查
     */
    _runtimeCheck() {
        const results = this._checkController.runtimeCheck(this.editResult);
        if (results.length > 0) {
            this._operationController.currentOperation.undo(); // 恢复到最近的操作

            const text = results.map(it => it.message).join('\n');
            this.refreshCenterInfo(text, 'error');

            return false;
        }

        return true;
    }

    /**
     * 执行完成时检查
     */
    _postCheck() {
        const results = this._checkController.postCheck(this.editResult);
        if (results.length > 0) {
            const text = results.map(it => it.message).join('\n');
            this.refreshCenterInfo(text, 'error');

            return false;
        }

        return true;
    }

    /**
     * 工具操作完成时调用
     */
    finish() {
        if (!this._postCheck()) {
            return;
        }

        if (Util.isFunction(this.onFinish)) {
            this.onFinish(this.editResult);
        }
    }

    /**
     * 设置editResult属性，禁止对editResult的属性值进行修改
     * @param {object} value editResult对象
     */
    resetEditResult(editResult) {
        this.editResult = editResult.clone();
    }

    /**
     * 创建操作
     * @param {string} description 操作说明
     */
    createOperation(description = '') {
        if (!this._runtimeCheck()) {
            return false;
        }

        const editResult = this.editResult.clone();
        const operation = new Operation(editResult, description, {
            onRedo: opn => {
                this.onRedo(opn);
            },
            onUndo: opn => {
                this.onUndo(opn);
            },
        });

        this._operationController.add(operation);

        this.refresh();

        return true;
    }

    onUndo(operation) {
        this.resetEditResult(operation.editResult);

        this.refresh();
    }

    onRedo(operation) {
        this.resetEditResult(operation.editResult);

        this.refresh();
    }

    undo() {
        this._operationController.undo();
    }

    redo() {
        this._operationController.redo();
    }
}
